import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSearchAvailableParticipantsQuery } from '../messaging-api-slice';
import { AvailableParticipantsList } from './available-participants-list';
import { SearchBar } from './search-bar';

const LARGE_LIST_THRESHOLD = 50;

interface ComposeMessageDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectConversation: (conversationId: number) => void;
  onStartConversation: (participantId: number) => Promise<void>;
}

export const ComposeMessageDialog: React.FC<ComposeMessageDialogProps> = ({
  open,
  onClose,
  onSelectConversation,
  onStartConversation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: participants = [], isLoading } = useSearchAvailableParticipantsQuery(
    { searchTerm },
    { skip: !open }
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const hasSearchTerm = searchTerm.length > 0;
  const showLargeListNotice = !hasSearchTerm && participants.length >= LARGE_LIST_THRESHOLD;

  const emptyState = hasSearchTerm ? 'no-matches' : 'no-participants';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Compose New Message</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search for a user..."
          />
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {showLargeListNotice && (
              <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {participants.length} recipients available. Use search to filter the list.
                </Typography>
              </Box>
            )}
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <AvailableParticipantsList
                participants={participants}
                emptyState={emptyState}
                searchTerm={searchTerm}
                onClearSearch={() => setSearchTerm('')}
                onSelect={conversationId => {
                  onSelectConversation(conversationId);
                  handleClose();
                }}
                onStartConversation={async participantId => {
                  try {
                    await onStartConversation(participantId);
                    handleClose();
                  } catch {
                    // Keep the dialog open so the user can retry or pick someone else.
                  }
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
